import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';
import axios from 'axios';
import { supabase } from '../supabase';
import { AIMemberLayout } from '../components/AIMemberLayout';

const MEMBER_NAME = 'hussain';

export default function Hussain() {
  const [activeTab, setActiveTab] = useState('briefing');
  const [memberStats, setMemberStats] = useState({ weekActions: 0, allTimeActions: 0 });
  const [recentActivity, setRecentActivity] = useState([]);
  const [showConfig, setShowConfig] = useState(false);
  const [focusArea, setFocusArea] = useState('');
  const [personality, setPersonality] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [loading, setLoading] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [weeklyInsights, setWeeklyInsights] = useState(null);
  const [prospectUrl, setProspectUrl] = useState('');
  const [prospectResearch, setProspectResearch] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [platformData, setPlatformData] = useState(null);

  useEffect(() => {
    fetchPlatformData();
    const weekAgo = new Date(Date.now() - 7*86400000).toISOString();
    Promise.all([
      supabase.from('team_briefings').select('id',{count:'exact',head:true}).eq('from_member',MEMBER_NAME),
      supabase.from('team_briefings').select('id',{count:'exact',head:true}).eq('from_member',MEMBER_NAME).gte('created_at',weekAgo),
      supabase.from('team_briefings').select('subject,created_at,to_member,priority').eq('from_member',MEMBER_NAME).order('created_at',{ascending:false}).limit(10),
      supabase.from('platform_settings').select('key,value').in('key',[`${MEMBER_NAME}_focus`,`${MEMBER_NAME}_personality`]),
    ]).then(([all, week, recent, cfg]) => {
      setMemberStats({ allTimeActions: all.count||0, weekActions: week.count||0 });
      setRecentActivity(recent.data||[]);
      const settings = (cfg.data||[]).reduce((m,r)=>{m[r.key]=r.value;return m;},{});
      setFocusArea(settings[`${MEMBER_NAME}_focus`]||'');
      setPersonality(settings[`${MEMBER_NAME}_personality`]||'');
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveConfig = async () => {
    setSavingConfig(true);
    await supabase.from('platform_settings').upsert([
      {key:`${MEMBER_NAME}_focus`,value:focusArea,updated_at:new Date().toISOString()},
      {key:`${MEMBER_NAME}_personality`,value:personality,updated_at:new Date().toISOString()},
    ],{onConflict:'key'});
    setSavingConfig(false);
    setShowConfig(false);
  };

  const fetchPlatformData = async () => {
    const [clientsRes, contactsRes, workflowsRes, messagesRes, approvalsRes] = await Promise.all([
      supabase.from('clients').select('*'),
      supabase.from('contacts').select('*'),
      supabase.from('workflows').select('*'),
      supabase.from('messages').select('*'),
      supabase.from('approvals').select('*'),
    ]);
    setPlatformData({
      clients: clientsRes.data || [],
      contacts: contactsRes.data || [],
      workflows: workflowsRes.data || [],
      messages: messagesRes.data || [],
      approvals: approvalsRes.data || [],
    });
  };

  const callHussain = async (prompt) => {
    const { data } = await axios.post(`${API_BASE}/hussain`, { prompt });
    return data.result || 'Unable to generate response.';
  };

  const generateBriefing = async () => {
    if (!platformData) return;
    setLoading(true);
    setBriefing(null);
    const prompt = `Generate a morning briefing for Sales Scales based on this real platform data:

Clients: ${platformData.clients.length} total (${platformData.clients.filter(c => c.status === 'live').length} live)
Contacts: ${platformData.contacts.length} total in database
Active Workflows: ${platformData.workflows.filter(w => w.status === 'active').length} of ${platformData.workflows.length} total
Messages Sent: ${platformData.messages.filter(m => m.direction === 'outbound').length}
Messages Received: ${platformData.messages.filter(m => m.direction === 'inbound').length}
Pending Approvals: ${platformData.approvals.filter(a => a.status === 'pending').length}
Client names: ${platformData.clients.map(c => c.name).join(', ')}

Write a sharp morning briefing covering:
1. What is the most important thing to focus on today
2. What the numbers are telling us
3. One specific action to take today
4. Any risks or opportunities to be aware of

Be direct and specific. No generic advice.`;
    const result = await callHussain(prompt);
    setBriefing(result);
    setLoading(false);
  };

  const generateWeeklyInsights = async () => {
    if (!platformData) return;
    setLoading(true);
    setWeeklyInsights(null);
    const prompt = `Generate a weekly strategic insights report for Sales Scales based on this platform data:

Clients: ${platformData.clients.length} total
Active Workflows: ${platformData.workflows.filter(w => w.status === 'active').length}
Total Messages Sent: ${platformData.messages.filter(m => m.direction === 'outbound').length}
Total Messages Received: ${platformData.messages.filter(m => m.direction === 'inbound').length}
Approvals approved: ${platformData.approvals.filter(a => a.status === 'approved').length}
Approvals rejected: ${platformData.approvals.filter(a => a.status === 'rejected').length}

Analyze this week's performance and provide:
1. What is working well
2. What is not working and why
3. The biggest opportunity right now
4. What to prioritize next week
5. A revenue growth prediction if current trajectory continues

Be brutally honest and data-driven.`;
    const result = await callHussain(prompt);
    setWeeklyInsights(result);
    setLoading(false);
  };

  const generateProspectResearch = async () => {
    if (!prospectUrl) return;
    setLoading(true);
    setProspectResearch(null);
    const prompt = `Research this ecommerce store and give me a prospect intelligence report: ${prospectUrl}

Provide:
1. What they sell and their likely target customer
2. Estimated monthly revenue range
3. What AI revenue systems they are likely missing
4. The biggest revenue gap we can fix for them
5. The perfect opening message to send them on LinkedIn
6. Confidence score (0-100) that they would be a good Sales Scales client

Be specific and actionable. This is a sales intelligence report.`;
    const result = await callHussain(prompt);
    setProspectResearch(result);
    setLoading(false);
  };

  const generateRecommendations = async () => {
    if (!platformData) return;
    setLoading(true);
    setRecommendations(null);
    const prompt = `Based on this Sales Scales platform data, give me strategic recommendations:

Clients: ${platformData.clients.length} (${platformData.clients.map(c => `${c.name} - ${c.status} - ${c.tier}`).join(', ')})
Contacts: ${platformData.contacts.length}
Active Workflows: ${platformData.workflows.filter(w => w.status === 'active').length}
Messages sent: ${platformData.messages.filter(m => m.direction === 'outbound').length}
Pending approvals: ${platformData.approvals.filter(a => a.status === 'pending').length}

Give me:
1. The 3 highest leverage actions to grow Sales Scales revenue right now
2. Which client needs the most attention and why
3. What automation to build next that would have the biggest impact
4. The ideal next client profile to target
5. One thing that could kill the business if not fixed

Rank everything by impact. Be direct.`;
    const result = await callHussain(prompt);
    setRecommendations(result);
    setLoading(false);
  };

  const tabs = [
    { id: 'briefing', label: 'Morning Briefing', icon: '☀' },
    { id: 'weekly', label: 'Weekly Insights', icon: '📊' },
    { id: 'prospect', label: 'Prospect Research', icon: '🔍' },
    { id: 'recommendations', label: 'Recommendations', icon: '🎯' },
  ];

  const ResultCard = ({ content }) => (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: 20, marginTop: 16, fontSize: 13, color: 'rgba(255,255,255,0.8)', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
      {content}
    </div>
  );

  const inStyle = { width:'100%', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'9px 12px', fontSize:12, color:'#f0f4f8', outline:'none', background:'rgba(255,255,255,0.05)', boxSizing:'border-box', fontFamily:'Inter,sans-serif' };

  return (
    <AIMemberLayout memberSlug={MEMBER_NAME} role="Intelligence & Strategy AI"
      description="Hussain analyzes all platform data and gives you sharp, actionable intelligence. He thinks like a founder — no fluff, only what matters."
      memberStats={memberStats} recentActivity={recentActivity}>

      {/* Configure panel */}
      {showConfig && (
        <div style={{ background: '#0f1f35', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 9, color: '#4a5568', letterSpacing: 2, fontWeight: 700, textTransform: 'uppercase', fontFamily:'DM Mono,monospace', marginBottom: 14 }}>Configure Hussain</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div><div style={{ fontSize: 10, color: '#8896a8', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase' }}>Focus Area</div>
              <input type="text" value={focusArea} onChange={e=>setFocusArea(e.target.value)} placeholder="e.g. Revenue growth..." style={inStyle} /></div>
            <div><div style={{ fontSize: 10, color: '#8896a8', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase' }}>Personality/Tone</div>
              <input type="text" value={personality} onChange={e=>setPersonality(e.target.value)} placeholder="e.g. More direct..." style={inStyle} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={saveConfig} disabled={savingConfig} style={{ background:'#c9a84c', color:'#0a1628', border:'none', borderRadius:8, padding:'9px 18px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'Inter,sans-serif' }}>{savingConfig?'Saving…':'Save'}</button>
            <button onClick={()=>setShowConfig(false)} style={{ background:'transparent', border:'1px solid rgba(255,255,255,0.1)', color:'#8896a8', borderRadius:8, padding:'9px 16px', fontSize:12, cursor:'pointer', fontFamily:'Inter,sans-serif' }}>Cancel</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid', fontSize: 12, cursor: 'pointer', fontWeight: activeTab === tab.id ? 700 : 400, background: activeTab === tab.id ? '#3b82f6' : 'rgba(255,255,255,0.04)', color: activeTab === tab.id ? '#fff' : '#8896a8', borderColor: activeTab === tab.id ? '#3b82f6' : 'rgba(255,255,255,0.08)', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6, fontFamily:'Inter,sans-serif' }}>
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {activeTab === 'briefing' && (
        <div style={{ background: '#0f1f35', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: 20 }}>
          <div style={{ fontSize: 9, color: '#4a5568', letterSpacing: 2, fontWeight: 700, textTransform: 'uppercase', fontFamily:'DM Mono,monospace', marginBottom: 6 }}>Daily Intelligence</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#f0f4f8', marginBottom: 6 }}>Morning Briefing</div>
          <div style={{ fontSize: 12, color: '#8896a8', marginBottom: 16 }}>Hussain analyzes your platform data and tells you exactly what to focus on today.</div>
          <button onClick={generateBriefing} disabled={loading}
            style={{ background:'#3b82f6', color:'#fff', border:'none', borderRadius:8, padding:'10px 20px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'Inter,sans-serif' }}>
            {loading ? 'Hussain is thinking...' : '☀ Generate Today\'s Briefing'}
          </button>
          {briefing && <ResultCard content={briefing} />}
        </div>
      )}

      {activeTab === 'weekly' && (
        <div style={{ background: '#0f1f35', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: 20 }}>
          <div style={{ fontSize: 9, color: '#4a5568', letterSpacing: 2, fontWeight: 700, textTransform: 'uppercase', fontFamily:'DM Mono,monospace', marginBottom: 6 }}>Strategic Analysis</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#f0f4f8', marginBottom: 6 }}>Weekly Insights</div>
          <div style={{ fontSize: 12, color: '#8896a8', marginBottom: 16 }}>A deep analysis of this week's performance — what worked, what didn't, and what to do next week.</div>
          <button onClick={generateWeeklyInsights} disabled={loading}
            style={{ background:'#3b82f6', color:'#fff', border:'none', borderRadius:8, padding:'10px 20px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'Inter,sans-serif' }}>
            {loading ? 'Hussain is analyzing...' : '📊 Generate Weekly Insights'}
          </button>
          {weeklyInsights && <ResultCard content={weeklyInsights} />}
        </div>
      )}

      {activeTab === 'prospect' && (
        <div style={{ background: '#0f1f35', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: 20 }}>
          <div style={{ fontSize: 9, color: '#4a5568', letterSpacing: 2, fontWeight: 700, textTransform: 'uppercase', fontFamily:'DM Mono,monospace', marginBottom: 6 }}>Sales Intelligence</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#f0f4f8', marginBottom: 6 }}>Prospect Research</div>
          <div style={{ fontSize: 12, color: '#8896a8', marginBottom: 16 }}>Enter a Shopify store URL and Hussain will give you a sales intelligence report.</div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input type="text" value={prospectUrl} onChange={e => setProspectUrl(e.target.value)}
              placeholder="https://store.myshopify.com"
              style={{ flex: 1, border: '1px solid #e4e9f0', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#0a1628', outline: 'none', fontFamily: 'DM Sans, sans-serif' }} />
            <button onClick={generateProspectResearch} disabled={loading || !prospectUrl}
              style={{ background: '#0a1628', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {loading ? 'Researching...' : '🔍 Research'}
            </button>
          </div>
          {prospectResearch && <ResultCard content={prospectResearch} />}
        </div>
      )}

      {activeTab === 'recommendations' && (
        <div style={{ background: '#0f1f35', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: 20 }}>
          <div style={{ fontSize: 9, color: '#4a5568', letterSpacing: 2, fontWeight: 700, textTransform: 'uppercase', fontFamily:'DM Mono,monospace', marginBottom: 6 }}>Strategic Direction</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#f0f4f8', marginBottom: 6 }}>Recommendations</div>
          <div style={{ fontSize: 12, color: '#8896a8', marginBottom: 16 }}>Hussain analyzes everything and tells you the highest leverage actions to take right now.</div>
          <button onClick={generateRecommendations} disabled={loading}
            style={{ background:'#c9a84c', color:'#0a1628', border:'none', borderRadius:8, padding:'10px 20px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'Inter,sans-serif' }}>
            {loading ? 'Hussain is thinking…' : '🎯 Generate Recommendations'}
          </button>
          {recommendations && <ResultCard content={recommendations} />}
        </div>
      )}
    </AIMemberLayout>
  );
}